import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button, Input, FormControl, Select, MenuItem, TextField } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { usePopup, type PopupAction, defPopupAction } from "@components/popup/popup_context";

import { useAuthStore } from "@user/store/auth_store";

interface State {
}

const defState = {
};

interface Info {
  pnNo: string,
  mngType: string,
  pn: string,
  kwd: string,
  ctg: string,
  uom: string,
  useYn: string,
  remark: string
}

const defInfo = {
  pnNo: "",
  mngType: "",
  pn: "",
  kwd: "",
  ctg: "",
  uom: "",
  useYn: "Y",
  remark: ""
};

const validationMsg = [
  {id: "pn", type: "NotBlank", msg: "PN을 입력해 주세요."},
  {id: "pn", type: "Size", msg: "PN을 확인해 주세요."},
  {id: "pn", type: "Duplicate", msg: "이미 등록된 PN입니다."},
  {id: "useYn", type: "NotBlank", msg: "사용여부를 선택해 주세요."},
  {id: "useYn", type: "Size", msg: "사용여부를 확인해 주세요."}
];

function PopupRegistPn(props: any) {
  const { setOption, setActions, onClose } = usePopup();
  const auth = useAuthStore(useShallow((state) => state.getAuth("/master/pn_manage_list")));
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  // 팝업 설정
  const saveRef = useRef(save);
  saveRef.current = save;
  useLayoutEffect(() => {
    setOption({
      title: "PN 등록",
      maxWidth: "700px"
    });
    setActions([
      {
        id: "closePopup",
        label: "취소",
        onClick: () => onClose(),
        variant: "outlined",
      },
      ...(auth.write ? [{
        id: "save",
        label: "저장",
        onClick: () => saveRef.current(),
        variant: "contained",
      } satisfies PopupAction] : []),
    ]);
  }, [auth.write]);

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    setInfo(prev => ({ ...prev, mngType: props.data.mngType }));
  }

  async function searchInfo() {
  }

  async function closePopup() {
    onClose();
  }

  async function save() {
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    if (await kutil.confirm("등록하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/master/pn_manage_detail.proc?mode=add", info, {
        validationMsg: validationMsg
      });
      await kutil.alert("등록하였습니다.");
      onClose({isSuccess: true, pn: data.info.pn});
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* 팝업 내용 */}
      <div className="popup-contents">
        {/* Cell 수정 */}
        <div className="table-wrap">
          <table>
            <colgroup>
              <col style={{ width: "17%" }} />
              <col style={{ width: "33%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "33%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row">P/N<em>*</em></th>
                <td>
                  <Input
                    inputProps={{ maxLength: 100 }}
                    value={info.pn}
                    onChange={(e) => {setInfo(prev => ({ ...prev, pn: e.target.value }))}}
                    onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                  />
                </td>
                <th scope="row">KWD</th>
                <td>
                  <Input
                    inputProps={{ maxLength: 100 }}
                    value={info.kwd}
                    onChange={(e) => {setInfo(prev => ({ ...prev, kwd: e.target.value }))}}
                    onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                  />
                </td>
              </tr>
              <tr>
                <th scope="row">CTG</th>
                <td>
                  <FormControl sx={{ m: 1, minWidth: "95%" }} size="small">
                    <Select
                      value={info.ctg}
                      onChange={(e) => {setInfo(prev => ({ ...prev, ctg: e.target.value }))}}
                      displayEmpty
                    >
                    <MenuItem value="">선택</MenuItem>
                    {(commonCode.ctg as any[]).map((code) => (
                      <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                    ))}
                    </Select>
                  </FormControl>
                </td>
                <th scope="row">UOM</th>
                <td>
                  <Input
                    inputProps={{ maxLength: 100 }}
                    value={info.uom}
                    onChange={(e) => {setInfo(prev => ({ ...prev, uom: e.target.value }))}}
                    onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                  />
                </td>
              </tr>
              <tr>
                <th scope="col" colSpan={4} className="ta-center">Remark</th>
              </tr>
              <tr>
                <td colSpan={4}>
                  <TextField
                    value={info.remark}
                    onChange={(e) => {setInfo(prev => ({ ...prev, remark: e.target.value }))}}
                    multiline
                    rows={4}
                    fullWidth 
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/*// Cell 수정 */}
      </div>
      {/*// 팝업 내용 */}
    </>
  );
}

export default React.memo(PopupRegistPn);
